import { AdminApi } from '../../api/admin'
import { ITEMS_PER_PAGE } from '../../stores/adminStore'
import {
    ListUsersQueryDto,
    ListDraftPostsQueryDto,
    AdminUserDto,
    AdminDraftPostDto,
    AdminDraftPostDetailedDto,
    GetOrgsResponseDto,
    AdminGetUserResponseDto,
    GetResearchResultsQueryDto,
    PaginatedResearchResultsDto,
} from '../../types/admin-types'
import { useQuery } from '@tanstack/react-query'

// Query key factory
const adminQueryKeys = {
    all: ['admin'] as const,
    users: (query?: ListUsersQueryDto) => [...adminQueryKeys.all, 'users', query] as const,
    user: (id: string | null) => [...adminQueryKeys.all, 'user', id] as const,
    draftPosts: (query?: ListDraftPostsQueryDto) =>
        [...adminQueryKeys.all, 'draftPosts', query] as const,
    draftPost: (id: string | null) => [...adminQueryKeys.all, 'draftPost', id] as const,
    orgs: () => [...adminQueryKeys.all, 'orgs'] as const,
    researchResults: (userId: string | null, query: GetResearchResultsQueryDto) =>
        [...adminQueryKeys.all, 'researchResults', userId, query] as const,
}

// Hook to fetch users (consider pagination/search later if needed)
export const useAdminUsers = (query?: ListUsersQueryDto) => {
    return useQuery<
        { users: AdminUserDto[]; total: number },
        Error,
        { users: AdminUserDto[]; total: number }
    >({
        queryKey: adminQueryKeys.users(query),
        queryFn: async () => {
            const response = await AdminApi.listUsers(query)
            return response.data // Axios automatically wraps in data
        },
        // Keep data fresh for 5 minutes, refetch in background if stale
        staleTime: 1000 * 60 * 5,
    })
}

// Hook to fetch a single detailed user
export const useAdminUser = (id: string | null) => {
    return useQuery<AdminGetUserResponseDto, Error>({
        queryKey: adminQueryKeys.user(id),
        queryFn: async () => {
            if (!id) {
                throw new Error('Cannot fetch user without an ID')
            }
            const response = await AdminApi.getUser(id)
            // Assuming the response directly contains the user object
            // Adjust if the backend wraps it, e.g., response.data.user
            return response.data
        },
        enabled: !!id, // Only run the query if the ID is not null or undefined
        staleTime: 1000 * 60 * 5, // Keep data fresh for 5 minutes
    })
}

// Hook to fetch draft posts with pagination
export const useAdminDraftPosts = (query?: ListDraftPostsQueryDto) => {
    return useQuery<
        {
            draft_posts: AdminDraftPostDto[]
            pagination: { total: number; page: number; limit: number }
        },
        Error // Specify Error type
    >({
        // Ensure query key reflects pagination/filters for proper caching
        queryKey: adminQueryKeys.draftPosts(query),
        queryFn: async () => {
            const response = await AdminApi.listDraftPosts(query)
            return response.data
        },
        staleTime: 1000 * 60 * 5, // Keep data fresh for 5 minutes
    })
}

// Hook to fetch a single detailed draft post
export const useAdminDraftPost = (id: string | null) => {
    return useQuery<
        { draft_post: AdminDraftPostDetailedDto }, // Backend response is wrapped
        Error // Specify Error type
    >({
        queryKey: adminQueryKeys.draftPost(id),
        queryFn: async () => {
            if (!id) {
                throw new Error('Cannot fetch draft post without an ID')
            }
            const response = await AdminApi.getDraftPost(id)
            return response.data // Response is { draft_post: ... }
        },
        enabled: !!id, // Only run the query if the ID is not null or undefined
        staleTime: 1000 * 60 * 5, // Keep data fresh for 5 minutes
    })
}

// Hook to fetch organizations
export const useAdminOrgs = () => {
    return useQuery<GetOrgsResponseDto, Error>({
        queryKey: adminQueryKeys.orgs(),
        queryFn: async () => {
            const response = await AdminApi.listOrgs()
            return response.data // Response is { orgs: [...] }
        },
        staleTime: 1000 * 60 * 15, // Orgs likely change less often, longer stale time
    })
}

// Hook to fetch research results for a user
export const useAdminResearchResults = (
    userId: string | null,
    query: GetResearchResultsQueryDto,
) => {
    const page = query?.page || 1
    const limit = query?.limit || ITEMS_PER_PAGE
    const queryParams = { page, limit }

    return useQuery<PaginatedResearchResultsDto, Error>({
        queryKey: adminQueryKeys.researchResults(userId, queryParams),
        queryFn: () => AdminApi.getResearchResults(userId!, queryParams).then((res) => res.data),
        enabled: !!userId, // Only run if userId is provided
        placeholderData: (previousData) => previousData,
        // keepPreviousData: true, // Consider implications
    })
}
